import { useState, useCallback, useEffect } from 'react';
import Cropper from 'react-easy-crop';
import { processImage, uploadToSupabase } from './imageProcessing';
import StandardButton from '../StandardButton';
import ConfirmationModal from '../ConfirmationModal';
import { supabase, supabaseFetch } from '../../../lib/supabase';
import { Upload, Image as ImageIcon, Check } from 'lucide-react';
import { validateImageFile } from '../../../utils/fileValidation';
/**
 * ImageCropper Modal Component
 * @param {boolean} isOpen - Modal visibility
 * @param {function} onClose - Close handler
 * @param {function} onSave - Save handler (receives { displayUrl, thumbnailUrl })
 * @param {string} aspectMode - 'hero' (4:3 with toggle) or 'inline' (square only)
 * @param {string} bucket - 'hero-images' or 'inline-images'
 * @param {string} userId - Current user ID
 */
export default function ImageCropper({
  isOpen,
  onClose,
  onSave,
  aspectMode = 'hero',
  bucket = 'hero-images',
  userId
}) {
  // Mode: 'choose' (initial), 'existing' (select from library), 'upload' (new upload)
  const [mode, setMode] = useState('choose');

  // Existing images state
  const [existingImages, setExistingImages] = useState([]);
  const [loadingImages, setLoadingImages] = useState(false);
  const [selectedExistingImage, setSelectedExistingImage] = useState(null);

  // Upload/crop state
  const [selectedFile, setSelectedFile] = useState(null);
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const aspectRatio = 4 / 3; // Fixed 4:3 ratio, sides cropped for square display
  const [isProcessing, setIsProcessing] = useState(false);
  const [modal, setModal] = useState({ visible: false, message: '', onOk: null });

  // Drag and drop state
  const [isDragging, setIsDragging] = useState(false);

  // Load user's existing images from both buckets
  // Uses supabaseFetch.storageList (direct fetch) because supabase.storage.list() hangs
  const loadExistingImages = useCallback(async () => {
    setLoadingImages(true);
    try {
      const allImages = [];

      // Get images from hero-images bucket (direct fetch, bypasses JS client)
      const { data: heroImages, error: heroError } = await supabaseFetch.storageList(
        'hero-images',
        `public/${userId}`,
        { limit: 50, sortBy: { column: 'created_at', order: 'desc' } }
      );

      if (heroError) console.error('Error loading hero images:', heroError);

      if (heroImages) {
        heroImages.forEach(img => {
          // Only include display images, not thumbnails
          if (img.name && img.name.includes('-display')) {
            const { data: urlData } = supabase.storage
              .from('hero-images')
              .getPublicUrl(`public/${userId}/${img.name}`);

            // Get thumbnail URL
            const thumbName = img.name.replace('-display', '-thumb');
            const { data: thumbData } = supabase.storage
              .from('hero-images')
              .getPublicUrl(`public/${userId}/${thumbName}`);

            allImages.push({
              id: img.id || img.name,
              name: img.name,
              displayUrl: urlData.publicUrl,
              thumbnailUrl: thumbData.publicUrl,
              bucket: 'hero-images',
              created_at: img.created_at
            });
          }
        });
      }

      // Get images from inline-images bucket (direct fetch, bypasses JS client)
      const { data: inlineImages, error: inlineError } = await supabaseFetch.storageList(
        'inline-images',
        `public/${userId}`,
        { limit: 50, sortBy: { column: 'created_at', order: 'desc' } }
      );

      if (inlineError) console.error('Error loading inline images:', inlineError);

      if (inlineImages) {
        inlineImages.forEach(img => {
          if (img.name && img.name.includes('-display')) {
            const { data: urlData } = supabase.storage
              .from('inline-images')
              .getPublicUrl(`public/${userId}/${img.name}`);

            const thumbName = img.name.replace('-display', '-thumb');
            const { data: thumbData } = supabase.storage
              .from('inline-images')
              .getPublicUrl(`public/${userId}/${thumbName}`);

            allImages.push({
              id: img.id || img.name,
              name: img.name,
              displayUrl: urlData.publicUrl,
              thumbnailUrl: thumbData.publicUrl,
              bucket: 'inline-images',
              created_at: img.created_at
            });
          }
        });
      }

      setExistingImages(allImages);
    } catch (error) {
      console.error('Error loading existing images:', error);
    } finally {
      setLoadingImages(false);
    }
  }, [userId]);

  // Load existing images when modal opens or mode changes to 'existing'
  useEffect(() => {
    if (isOpen && userId && mode === 'existing') {
      loadExistingImages();
    }
  }, [isOpen, userId, mode, loadExistingImages]);

  // File selection with enhanced validation (magic bytes)
  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Quick MIME type check first (skip if empty — Android sometimes omits file.type)
    if (file.type && !file.type.startsWith('image/')) {
      setModal({
        visible: true,
        message: 'Please select an image file.',
        onOk: () => setModal({ visible: false, message: '', onOk: null })
      });
      return;
    }

    // Enhanced validation with magic bytes to prevent malicious file uploads
    const validation = await validateImageFile(file);
    if (!validation.valid) {
      setModal({
        visible: true,
        message: validation.error || 'Invalid image file.',
        onOk: () => setModal({ visible: false, message: '', onOk: null })
      });
      return;
    }

    setSelectedFile(file);

    // Preview
    const reader = new FileReader();
    reader.onload = () => setImageSrc(reader.result);
    reader.readAsDataURL(file);
  };

  // Process a file (shared by file select and drag-drop)
  const processFile = async (file) => {
    if (!file) return;

    // Quick MIME type check first (skip if empty — Android sometimes omits file.type)
    if (file.type && !file.type.startsWith('image/')) {
      setModal({
        visible: true,
        message: 'Please select an image file.',
        onOk: () => setModal({ visible: false, message: '', onOk: null })
      });
      return;
    }

    // Enhanced validation with magic bytes to prevent malicious file uploads
    const validation = await validateImageFile(file);
    if (!validation.valid) {
      setModal({
        visible: true,
        message: validation.error || 'Invalid image file.',
        onOk: () => setModal({ visible: false, message: '', onOk: null })
      });
      return;
    }

    setSelectedFile(file);

    // Preview
    const reader = new FileReader();
    reader.onload = () => setImageSrc(reader.result);
    reader.readAsDataURL(file);
  };

  // Drag and drop handlers
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      await processFile(files[0]);
    }
  };

  // Crop complete callback
  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  // Save and process image
  const handleSave = async () => {
    if (!selectedFile || !croppedAreaPixels) return;

    // Check for required props
    if (!userId) {
      console.error('ImageCropper: userId is required for upload');
      setModal({
        visible: true,
        message: 'User authentication is required to upload images.',
        onOk: () => setModal({ visible: false, message: '', onOk: null })
      });
      return;
    }

    setIsProcessing(true);

    try {
      console.log('Starting image processing...');

      // 1. Process image (crop + resize + compress)
      const { displayBlob, thumbnailBlob, fileName } = await processImage(
        selectedFile,
        croppedAreaPixels,
        aspectRatio === 1 ? 'square' : '4:3'
      );

      console.log('Image processed, uploading to Supabase...', { bucket, userId });

      // 2. Upload to Supabase
      const { displayUrl, thumbnailUrl } = await uploadToSupabase(
        displayBlob,
        thumbnailBlob,
        fileName,
        bucket,
        userId
      );

      console.log('Upload successful:', { displayUrl });

      // 3. Return URLs to parent
      onSave({
        displayUrl,
        thumbnailUrl,
        aspectRatio: aspectRatio === 1 ? 'square' : '4:3'
      });

      // 4. Reset and close
      resetState();
      onClose();

    } catch (error) {
      console.error('Image processing error:', error);
      setModal({
        visible: true,
        message: error.message || 'Failed to process image. Please try again.',
        onOk: () => setModal({ visible: false, message: '', onOk: null })
      });
    } finally {
      setIsProcessing(false);
    }
  };

  // Reset state
  const resetState = () => {
    setMode('choose');
    setSelectedFile(null);
    setImageSrc(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
    setSelectedExistingImage(null);
  };

  // Cancel
  const handleCancel = () => {
    resetState();
    onClose();
  };

  // Handle selecting an existing image
  const handleSelectExistingImage = (image) => {
    setSelectedExistingImage(image);
  };

  // Use the selected existing image
  const handleUseExistingImage = () => {
    if (!selectedExistingImage) return;

    onSave({
      displayUrl: selectedExistingImage.displayUrl,
      thumbnailUrl: selectedExistingImage.thumbnailUrl,
      aspectRatio: '4:3'
    });

    resetState();
    onClose();
  };

  // Go back to choose mode
  const handleBack = () => {
    setMode('choose');
    setSelectedExistingImage(null);
    setSelectedFile(null);
    setImageSrc(null);
  };

  if (!isOpen) return null;

  // Get title based on mode
  const getTitle = () => {
    if (mode === 'choose') return 'Select Image';
    if (mode === 'existing') return 'My Images';
    return aspectMode === 'hero' ? 'Crop Hero Image' : 'Crop Inline Image';
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.8)',
      zIndex: 9999,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        width: '90%',
        maxWidth: '700px',
        maxHeight: '90vh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}>
        {/* Header */}
        <div style={{ padding: '24px 24px 0 24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
            {mode !== 'choose' && (
              <button
                onClick={handleBack}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '20px',
                  cursor: 'pointer',
                  padding: '4px 8px',
                  borderRadius: '4px'
                }}
              >
                ←
              </button>
            )}
            <h2 style={{ fontSize: '24px', fontWeight: 'bold', margin: 0 }}>
              {getTitle()}
            </h2>
          </div>
        </div>

        {/* Scrollable Content */}
        <div style={{
          flex: 1,
          overflowY: 'auto',
          padding: '0 24px',
          minHeight: 0
        }}>

        {/* MODE: Choose between existing or upload */}
        {mode === 'choose' && (
          <div style={{ paddingBottom: '20px' }}>
            <p style={{
              fontSize: '14px',
              color: '#6b7280',
              marginBottom: '20px',
              textAlign: 'center'
            }}>
              {'Choose from your image library or upload a new image'}
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {/* Select from My Images - Recommended */}
              <button
                onClick={() => setMode('existing')}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '16px',
                  padding: '20px',
                  backgroundColor: '#fdf2f8',
                  border: '2px solid #BB4D9A',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  textAlign: 'left',
                  transition: 'all 0.2s'
                }}
              >
                <div style={{
                  width: '56px',
                  height: '56px',
                  backgroundColor: '#BB4D9A',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <ImageIcon size={28} color="white" />
                </div>
                <div style={{ flex: 1 }}>
                  <p style={{ fontSize: '18px', fontWeight: '600', color: '#374151', margin: 0 }}>
                    {'Select from My Images'}
                  </p>
                  <p style={{ fontSize: '13px', color: '#6b7280', margin: '4px 0 0 0' }}>
                    {'Choose from images you\'ve already uploaded'}
                  </p>
                </div>
              </button>

              {/* Upload New Image */}
              <button
                onClick={() => setMode('upload')}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '16px',
                  padding: '20px',
                  backgroundColor: '#f9fafb',
                  border: '2px solid #e5e7eb',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  textAlign: 'left',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#BB4D9A';
                  e.currentTarget.style.backgroundColor = '#fdf2f8';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#e5e7eb';
                  e.currentTarget.style.backgroundColor = '#f9fafb';
                }}
              >
                <div style={{
                  width: '56px',
                  height: '56px',
                  backgroundColor: '#6b7280',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Upload size={28} color="white" />
                </div>
                <div>
                  <p style={{ fontSize: '18px', fontWeight: '600', color: '#374151', margin: 0 }}>
                    {'Upload New Image'}
                  </p>
                  <p style={{ fontSize: '13px', color: '#6b7280', margin: '4px 0 0 0' }}>
                    {'Select and crop a new image'}
                  </p>
                </div>
              </button>
            </div>
          </div>
        )}

        {/* MODE: Select from existing images */}
        {mode === 'existing' && (
          <div style={{ paddingBottom: '20px' }}>
            {loadingImages ? (
              <div style={{
                textAlign: 'center',
                padding: '40px',
                color: '#6b7280'
              }}>
                {'Loading images...'}
              </div>
            ) : existingImages.length === 0 ? (
              <div style={{
                textAlign: 'center',
                padding: '40px',
                color: '#6b7280',
                backgroundColor: '#f9fafb',
                borderRadius: '12px',
                border: '2px dashed #e5e7eb'
              }}>
                <ImageIcon size={48} color="#BB4D9A" style={{ marginBottom: '16px' }} />
                <p style={{ fontSize: '16px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
                  {'No images yet'}
                </p>
                <p style={{ fontSize: '14px', marginBottom: '16px' }}>
                  {'Upload your first image to get started'}
                </p>
                <button
                  onClick={() => setMode('upload')}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#BB4D9A',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  {'Upload Image'}
                </button>
              </div>
            ) : (
              <>
                <p style={{
                  fontSize: '13px',
                  color: '#6b7280',
                  marginBottom: '12px'
                }}>
                  {`Click an image to select it (${existingImages.length} image${existingImages.length !== 1 ? 's' : ''})`}
                </p>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
                  gap: '12px'
                }}>
                  {existingImages.map((image) => (
                    <div
                      key={image.id}
                      onClick={() => handleSelectExistingImage(image)}
                      style={{
                        position: 'relative',
                        aspectRatio: '1 / 1',
                        borderRadius: '8px',
                        overflow: 'hidden',
                        cursor: 'pointer',
                        border: selectedExistingImage?.id === image.id
                          ? '3px solid #BB4D9A'
                          : '2px solid #e5e7eb',
                        transition: 'all 0.2s'
                      }}
                    >
                      <img
                        src={image.thumbnailUrl || image.displayUrl}
                        alt="Uploaded"
                        loading="lazy"
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                      />
                      {selectedExistingImage?.id === image.id && (
                        <div style={{
                          position: 'absolute',
                          top: '8px',
                          right: '8px',
                          width: '24px',
                          height: '24px',
                          backgroundColor: '#BB4D9A',
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <Check size={14} color="white" />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {/* MODE: Upload new image */}
        {mode === 'upload' && !imageSrc && (
          <div
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            style={{
              border: isDragging ? '3px dashed #BB4D9A' : '2px dashed #ccc',
              borderRadius: '12px',
              padding: '40px',
              textAlign: 'center',
              cursor: 'pointer',
              marginBottom: '20px',
              backgroundColor: isDragging ? '#fdf2f8' : '#f9fafb',
              transition: 'all 0.2s ease'
            }}
          >
            <input
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              style={{ position: 'absolute', width: 0, height: 0, overflow: 'hidden', opacity: 0 }}
              id="image-upload"
            />
            <label htmlFor="image-upload" style={{ cursor: 'pointer', display: 'block' }}>
              {isDragging ? (
                <>
                  <div style={{ fontSize: '48px', marginBottom: '16px' }}>📥</div>
                  <div style={{ fontSize: '18px', color: '#BB4D9A', fontWeight: '600' }}>
                    {'Drop image here!'}
                  </div>
                </>
              ) : (
                <>
                  <div style={{ fontSize: '48px', marginBottom: '16px' }}>📷</div>
                  <div style={{ fontSize: '16px', color: '#374151', fontWeight: '500' }}>
                    {'Drag and drop an image here'}
                  </div>
                  <div style={{ fontSize: '14px', color: '#6b7280', marginTop: '8px' }}>
                    {'or click to browse files'}
                  </div>
                  <div style={{ fontSize: '13px', color: '#9ca3af', marginTop: '12px' }}>
                    {'Max 10MB · JPG, PNG, WebP, GIF'}
                  </div>
                </>
              )}
            </label>
          </div>
        )}

        {/* MODE: Cropping uploaded image */}
        {mode === 'upload' && imageSrc && (
          <>
            <div style={{
              position: 'relative',
              width: '100%',
              aspectRatio: '4 / 3',
              backgroundColor: '#f0f0f0',
              borderRadius: '8px',
              marginBottom: '20px'
            }}>
              <Cropper
                image={imageSrc}
                crop={crop}
                zoom={zoom}
                aspect={aspectRatio}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
                showGrid={false}
                restrictPosition={false}
                style={{
                  containerStyle: {
                    borderRadius: '8px'
                  }
                }}
              />
              {/* Square safe-zone guide lines — vertical lines at 12.5% from each edge
                  marking the centre square of the 4:3 frame (what's visible in thumbnails/cards) */}
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                pointerEvents: 'none',
                zIndex: 3
              }}>
                <div style={{
                  position: 'absolute',
                  left: '12.5%',
                  top: 0,
                  bottom: 0,
                  width: '2px',
                  backgroundColor: 'rgba(255, 255, 255, 0.5)',
                  boxShadow: '0 0 3px rgba(0, 0, 0, 0.3)'
                }} />
                <div style={{
                  position: 'absolute',
                  right: '12.5%',
                  top: 0,
                  bottom: 0,
                  width: '2px',
                  backgroundColor: 'rgba(255, 255, 255, 0.5)',
                  boxShadow: '0 0 3px rgba(0, 0, 0, 0.3)'
                }} />
              </div>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                {'Zoom'}
              </label>
              <input
                type="range"
                min={0.5}
                max={3}
                step={0.1}
                value={zoom}
                onChange={(e) => setZoom(Number(e.target.value))}
                style={{ width: '100%' }}
              />
              <p style={{
                fontSize: '12px',
                color: '#6b7280',
                marginTop: '6px',
                textAlign: 'center'
              }}>
                {'The area between the guide lines is what will appear in square thumbnails and cards'}
              </p>
            </div>
          </>
        )}
        </div>

        {/* Fixed Footer with Buttons - Always Visible */}
        <div style={{
          borderTop: '2px solid #e5e7eb',
          padding: '20px 24px',
          display: 'flex',
          gap: '12px',
          justifyContent: 'flex-end',
          backgroundColor: '#f9fafb',
          flexShrink: 0,
          boxShadow: '0 -2px 8px rgba(0,0,0,0.1)',
          position: 'relative',
          zIndex: 10
        }}>
          <StandardButton
            variant="utility"
            iconName="X"
            label="Cancel"
            onClick={handleCancel}
            disabled={isProcessing}
          />
          {/* Show "Use This Image" button when existing image is selected */}
          {mode === 'existing' && selectedExistingImage && (
            <StandardButton
              variant="action"
              iconName="Check"
              label="Use This Image"
              onClick={handleUseExistingImage}
            />
          )}
          {/* Show "Save Image" button when cropping */}
          {mode === 'upload' && imageSrc && (
            <StandardButton
              variant="action"
              iconName="Save"
              label={isProcessing ? 'Processing…' : 'Save Image'}
              onClick={handleSave}
              disabled={isProcessing}
            />
          )}
        </div>
      </div>

      {/* Confirmation Modal for errors */}
      <ConfirmationModal
        visible={modal.visible}
        title="Notice"
        message={modal.message}
        onOk={() => {
          if (modal.onOk) modal.onOk();
        }}
        onCancel={() => setModal({ visible: false, message: '', onOk: null })}
      />
    </div>
  );
}
